import Bugsnag from '@bugsnag/js';
import * as qsLib from 'qs';

import { setJiraMetadataForBugsnag } from '../bugsnag';

export default class JiraServerClient {
  constructor() {
    const pluginVersion = this.getPluginVersion();
    this._client = window.___JiraServerRawClient;
    this._jiraVersionNumbers = this.getServerInfo()
      .then(({ version, versionNumbers, deploymentType }) => {
        this._metadata = {
          version,
          pluginVersion,
          type: deploymentType,
        };
        //TODO: Consider removing this call, have Bugsnag call getMetadata instead
        setJiraMetadataForBugsnag(this._metadata);
        return versionNumbers;
      }).catch(() => null);

    this._createMetadataCache = {
      projects: null,
      issueTypes: {},
      fields: {}
    };
  }

  isFeatureFlagsEnabled() {
    const urlParams = new URLSearchParams(window.location.search);
    const showFF = urlParams.get('showFeatureFlags');
    return showFF === 'true';
  }
  getPluginVersion() {
    const urlParams = new URLSearchParams(window.location.search);
    const pluginVersion = urlParams.get('pluginVersion');
    return pluginVersion;
  }

  getMetadata() { return this._metadata; }

  arrayToObject(array, keyField) {
    return array.reduce((obj, item) => {
      obj[item[keyField]] = item;
      return obj;
    }, {});
  }

  async getContext() {
    const urlParams = new URLSearchParams(window.location.search);
    const issueKey = urlParams.get('issueKey');
    const issueTypeId = urlParams.get('issueTypeId');
    const clientKey = urlParams.get('clientKey');
    const projectKey = urlParams.get('project');
    const location = urlParams.get('location');
    const browseBaseUrl = await this._client.getBrowseBaseUrl();

    return {
      issueKey,
      projectKey,
      issueTypeId,
      clientKey,
      location,
      browseBaseUrl,
    };
  }

  async getJiraSession() {
    return this._request({
      method: 'GET',
      url: '/rest/auth/latest/session',
    });
  }

  async getSettings(names) {
    return this._request({
      method: 'GET',
      url: `/plugins/servlet/pagerduty/settings?${names.map(name => `keys[]=${name}`).join('&')}`
    });
  }

  removeSettings(names) {
    return this._request({
      method: 'DELETE',
      url: `/plugins/servlet/pagerduty/settings?${names.map(name => `keys[]=${name}`).join('&')}`
    });
  }

  setSettings(values) {
    return this._request({
      method: 'POST',
      url: '/plugins/servlet/pagerduty/settings',
      body: {
        settings: values
      }
    });
  }

  async getIssue(key) {
    return this._request({
      method: 'GET',
      url: `/rest/api/latest/issue/${key}?expand=renderedFields`
    });
  }

  getIssueProperty(issueKey, propertyName) {
    return this._request({
      method: 'GET',
      url: `/rest/api/latest/issue/${issueKey}/properties/${propertyName}`,
    })
      .catch(e => {
        console.log('suppressing getIssueProperty error:', e.message);
        return { value: null };
      })
      .then(({ value }) => value);
  }

  setIssueProperty(issueKey, propertyName, body) {
    return this._request({
      method: 'PUT',
      url: `/rest/api/latest/issue/${issueKey}/properties/${propertyName}`,
      body,
    });
  }

  getProjectProperty(projectKey, propertyName) {
    return this._request({
      method: 'GET',
      url: `/rest/api/latest/project/${projectKey}/properties/${propertyName}`,
    })
      .catch(e => {
        console.log('suppressing getProjectProperty error:', e.message);
        return { value: null };
      })
      .then(({ value }) => value);
  }

  setProjectProperty(projectKey, propertyName, body) {
    return this._request({
      method: 'PUT',
      url: `/rest/api/latest/project/${projectKey}/properties/${propertyName}`,
      body,
    });
  }

  getUsers(filter) {
    return this._request({
      method: 'GET',
      url: `/rest/api/latest/user/search`,
      qs: { username: filter || '' }
    });
  }

  getGroups(filter) {
    return this._request({
      method: 'GET',
      url: `/rest/api/latest/groups/picker`,
      qs: { query: filter || undefined, maxResults: 100 }
    }).then(response => response.groups);
  }

  getUserByName(username) {
    return this._request({
      method: 'GET',
      url: `/rest/api/latest/user`,
      qs: { username }
    });
  }

  getProjects() {
    return this._request({
      method: 'GET',
      url: '/rest/api/latest/project'
    });
  }

  async getCashedProjects() {
    if (!this._createMetadataCache.projects) {
      this._createMetadataCache.projects = await this.getProjects();
    }
    return [...this._createMetadataCache.projects];
  }

  getProjectIssueTypesPaginated({ projectId, maxResults = 50, startAt = 0 }) {
    return this._request({
      method: 'GET',
      qs: { maxResults, startAt },
      url: `/rest/api/2/issue/createmeta/${projectId}/issuetypes`
    });
  }

  getIssueTypeFieldsPaginated({ projectId, issueTypeId, maxResults = 50, startAt = 0 }) {
    return this._request({
      method: 'GET',
      qs: { maxResults, startAt },
      url: `/rest/api/2/issue/createmeta/${projectId}/issuetypes/${issueTypeId}`
    });
  }

  /**
   * This method can paginate through responses and get all values.
   * A response should have the next template:
   * {
   *  isLast: bool,
   *  maxResults: int,
   *  startAt: int,
   *  total: int,
   *  values: Array
   * }
   *  @param {function} requestWithPagination
   *  @param {Object}   initialParams
   */
  async getAllFromPaginatedRequest(requestWithPagination, initialParams) {
    let startAt = 0;
    const firstBatch = await requestWithPagination(initialParams);
    const allResults = firstBatch.values;
    let isLast = firstBatch.isLast;

    while (!isLast) {
      startAt = startAt + 50;
      const batchResult = await requestWithPagination({ ...initialParams, startAt });

      allResults.push(...batchResult.values);
      isLast = batchResult.isLast;
    }
    return allResults;
  }

  getProjectIssueTypes(projectId) {
    return this.getAllFromPaginatedRequest(this.getProjectIssueTypesPaginated.bind(this), { projectId });
  }

  getIssueTypeFields(projectId, issueTypeId) {
    return this.getAllFromPaginatedRequest(this.getIssueTypeFieldsPaginated.bind(this), { projectId, issueTypeId });
  }

  getServerInfo() {
    return this._request({
      method: 'GET',
      url: '/rest/api/2/serverInfo'
    });
  }

  _cacheNotExpandedMetadata = null;

  _getFilteredProjects(selectedProjects) {
    const allProjects = [...this._createMetadataCache.projects];
    const selectedProjectIds = selectedProjects.map(p => p.id);
    return allProjects.filter(project => selectedProjectIds.includes(project.id));
  }

  async _setIssueTypesInProjects(projects) {
    for (const project of projects) {
      if (!this._createMetadataCache.issueTypes[project.id]) {
        this._createMetadataCache.issueTypes[project.id] = await this.getProjectIssueTypes(project.id);
      }
      project.issuetypes = [...this._createMetadataCache.issueTypes[project.id]];
    }
  }

  async _setIssuesTypesAndFieldsInProjects(projects, selectedIssueTypes) {
    const selectedIssueTypeIds = selectedIssueTypes.map(i => i.id);
    for (const project of projects) {
      if (!this._createMetadataCache.issueTypes[project.id]) {
        this._createMetadataCache.issueTypes[project.id] = await this.getProjectIssueTypes(project.id);
      }
      const allIssueTypes = [...this._createMetadataCache.issueTypes[project.id]];
      for (const issueType of allIssueTypes) {
        if (selectedIssueTypeIds.includes(issueType.id)) {
          if (!this._createMetadataCache.fields[`${project.id}/${issueType.id}`]) {
            this._createMetadataCache.fields[`${project.id}/${issueType.id}`] = await this.getIssueTypeFields(project.id, issueType.id);
          }
          const issueFields = [...this._createMetadataCache.fields[`${project.id}/${issueType.id}`]];
          issueType.fields = this.arrayToObject(issueFields, 'fieldId');
        }
      }
      project.issuetypes = allIssueTypes;
    }
  }

  async _getCreateMetadataNewApi(selectedProjects = [], selectedIssueTypes = []) {
    if (!this._createMetadataCache.projects) {
      this._createMetadataCache.projects = await this.getProjects();
    }

    if (selectedProjects?.length && !selectedIssueTypes?.length) {
      const projects = this._getFilteredProjects(selectedProjects);
      await this._setIssueTypesInProjects(projects);
      return { expand: 'projects', projects };
    } else if (selectedProjects?.length && selectedIssueTypes?.length) {
      const projects = this._getFilteredProjects(selectedProjects);
      await this._setIssuesTypesAndFieldsInProjects(projects, selectedIssueTypes);
      return { expand: 'projects', projects };
    } else {
      return { expand: 'projects', projects: [...this._createMetadataCache.projects] };
    }
  }

  _getCreateMetadataOldApi(selectedProjects = [], selectedIssueTypes = []) {
    const expandMetadata = Boolean(selectedProjects?.length) && Boolean(selectedIssueTypes?.length);
    const makeGETstring = (projects, issueTypes) => `?expand=projects.issuetypes.fields&projectIds=${projects.map(p => p.id).join(',')}&issuetypeIds=${issueTypes.map(i => i.id).join(',')}`;

    if (expandMetadata) {
      return this._request({
        method: 'GET',
        url: `/rest/api/2/issue/createmeta${makeGETstring(selectedProjects, selectedIssueTypes)}`
      });
    } else {
      if (!this._cacheNotExpandedMetadata) {
        this._cacheNotExpandedMetadata = this._request({
          method: 'GET',
          url: '/rest/api/2/issue/createmeta'
        });
      }
      return this._cacheNotExpandedMetadata;
    }
  }

  async getCreateMetadata(selectedProjects = [], selectedIssueTypes = []) {
    const [major, minor] = await this._jiraVersionNumbers || [0, 0];

    if (major >= 9 || (major === 8 && minor >= 4)) {
      return this._getCreateMetadataNewApi(selectedProjects, selectedIssueTypes);
    } else {
      return this._getCreateMetadataOldApi(selectedProjects, selectedIssueTypes);
    }
  }

  getProjectStatuses(projectKey) {
    return this._request({
      method: 'GET',
      url: `/rest/api/2/project/${projectKey}/statuses`
    });
  }

  async getWebhooks() {
    const [major] = await this._jiraVersionNumbers;
    if (major >= 10) {
      return this._request({ method: 'GET', url: '/rest/jira-webhook/1.0/webhooks' });
    } else {
      return this._request({ method: 'GET', url: '/rest/webhooks/1.0/webhook' });
    }
  }

  async createWebhook(webhook) {
    const { name, events, jql, url } = webhook;
    const [major] = await this._jiraVersionNumbers;
    if (major >= 10) {
      return this._request({ method: 'POST',
        url: '/rest/jira-webhook/1.0/webhooks',
        body: {
          name,
          events,
          url,
          configuration: {
            FILTERS: jql
          }
        } });
    } else {
      return this._request({ method: 'POST',
        url: '/rest/webhooks/1.0/webhook',
        body: {
          name,
          events,
          url,
          filters: { 'issue-related-events-section': jql }
        } });
    }
  }

  async removeWebhook(webhookId) {
    const [major] = await this._jiraVersionNumbers;
    if (major >= 10) {
      return this._request({ method: 'DELETE', url: `/rest/jira-webhook/1.0/webhooks/${webhookId}` });
    } else {
      return this._request({ method: 'DELETE', url: `/rest/webhooks/1.0/webhook/${webhookId}` });
    }
  }

  searchByAutocompleteUrl(url, filter) {
    return this._request({
      method: 'GET',
      url: `${url}${encodeURIComponent(filter || '')}`
    });
  }

  loadAllCustomerOrganizationsCache = null;

  loadAllCustomerOrganizations() {
    if (!this.loadAllCustomerOrganizationsCache) {
      this.loadAllCustomerOrganizationsCache = (async () => {
        let more = true;
        let res = [];

        while (more) {
          const { values, isLastPage } = await this._request({
            method: 'GET',
            url: `/rest/servicedeskapi/organization?start=${res.length}`,
            headers: {
              'X-ExperimentalApi': 'opt-in'
            }
          });

          res = [...res, ...values];
          more = isLastPage === false && values.length > 0;
        }

        return res;
      })();
    }
    return this.loadAllCustomerOrganizationsCache;
  }

  resize({ width, height }) {
    this._client.resize({ width, height });
  }

  openModal(url, { width = '50vw', height, minHeight } = {}) {
    if (url.indexOf('/') === 0) {
      url = window.location.origin + url;
    }
    const params = [];
    params.push(`location=modal`);

    if (!minHeight) minHeight = 150;
    if (!height && minHeight) height = `${minHeight}px`;
    if (minHeight) {
      params.push(`minHeight=${minHeight}`);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const issueKey = urlParams.get('issueKey');
    const issueTypeId = urlParams.get('issueTypeId');
    const clientKey = urlParams.get('clientKey');

    if (issueKey) params.push(`issueKey=${issueKey}`);
    if (issueTypeId) params.push(`issueTypeId=${issueTypeId}`);
    if (clientKey) params.push(`clientKey=${clientKey}`);

    url = url + (url.includes('?') ? '&' : '?') + params.join('&');

    return this._client.openModal(url, { width, height });
  }

  closeCurrentModal() {
    this._client.closeCurrentModal();
  }

  confirm(text) {
    return this._client.confirm(text);
  }

  generateSignature({ subject }) {
    return this._request({
      method: 'GET',
      url: '/plugins/servlet/pagerduty/sign',
      qs: { subject }
    }).then(response => response.signature);
  }

  proxyRequest(method, url, body, headers, interpolateBody = false) {
    if (body && typeof body === 'object') {
      headers = {
        'Content-Type': 'application/json',
        ...headers,
      };
      body = JSON.stringify(body);
    }

    return this._request({
      method: 'POST',
      skipBugsnag: true,
      url: '/plugins/servlet/pagerduty/proxy',
      body: {
        url,
        method,
        body,
        headers,
        interpolateBody
      }
    }).then(response => {
      Bugsnag.leaveBreadcrumb('Proxy request', {
        request: `${method} ${url}`,
        status: response && response.status
      });

      return response;
    });
  }

  async _request({ method, url, qs, body = null, headers = {}, skipBugsnag = false }) {
    const query = qsLib.stringify(qs, { arrayFormat: 'brackets' });
    if (query) {
      if (url.includes('?')) url = `${url}&${query}`;
      else url = `${url}?${query}`;
    }

    let { status, responseText, error } =
      await this
        ._client
        .request({ method, url, body, headers })
        .catch(e => ({ error: e }));

    if (!skipBugsnag || error) {
      Bugsnag.leaveBreadcrumb('Cross-iframe request', {
        request: `${method} ${url}`,
        status,
        error,
      });
    }

    const successStatus = (String(status))[0] === '2';
    if (successStatus) {
      if (!responseText) return null;
      try {
        return JSON.parse(responseText);
      } catch (e) {
        Bugsnag.leaveBreadcrumb('Parsing responseText as JSON', { responseText });
        error = e;
      }
    }

    if (error) throw error;
    if (!responseText) {
      let baseUrl = await this._client.getBrowseBaseUrl().catch(error => 'unknown');
      throw new Error(`Invalid empty response from Jira, status = ${status}. Jira URL = ${baseUrl}`);
    }
    let responseJson = null;
    try {
      responseJson = JSON.parse(responseText);
    } catch (e) {
    }
    if (!responseJson) throw new Error(`Invalid response from Jira, status = ${status}`);

    if (responseJson && typeof responseJson.error === 'string') {
      throw new Error(responseJson.error);
    }

    const messages = [];
    if (responseJson && Array.isArray(responseJson.errorMessages)) {
      responseJson
        .errorMessages
        .filter(m => typeof m === 'string')
        .forEach(m => messages.push(m));
    }

    if (responseJson && responseJson.errors) {
      Object
        .entries(responseJson.errors)
        .filter(([k, v]) => typeof v === 'string')
        .forEach(([k, v]) => messages.push(`${k}: ${v}`));
    }

    if (messages.length > 0) throw new Error(messages.join(', '));
    throw new Error(JSON.stringify(responseJson));
  }
  /**
 * Set PD integration sidebar visibility for list of projects
 * @param {*} projects
 * @returns
 */
  async setSidebarVisibility(projects, visibility) {
    for (const project of projects) {
      const projectProperties = await this.getProjectProperty(project.key, 'pagerduty');
      if (projectProperties?.hideSidebar !== visibility) { await this.setProjectProperty(project.key, 'pagerduty', { ...projectProperties, hideSidebar: visibility }); }
    }
  }
}
